import { useEffect, useState } from "react";
import GoogleAd from "./GoogleAd";
import AdCreative from "./AdCreative";
import type { BannerAd } from "../../utils/homeData";

import SquareAds from "../../assets/images/SquareAds.webp";
import DoubleAds from "../../assets/images/DoubleAds.webp";

const SIDE_SQUARE_SLOT_1 = "4212484119";
const SIDE_SQUARE_SLOT_2 = "2899402443";

// A page can hold two instances (the sidebar one and the inline copy that
// replaces it where the sidebar is hidden); both share one request.
const inflight = new Map<string, Promise<any>>();
const fetchAdsOnce = (url: string) => {
  let pending = inflight.get(url);
  if (!pending) {
    pending = fetch(url).then((res) => res.json());
    pending.catch(() => inflight.delete(url));
    inflight.set(url, pending);
  }
  return pending;
};

type SideAd = BannerAd & { adType?: string | null };

type Props = {
  type: string;
  subLocation: string;
  /** One AdSense fallback slot instead of two (stacked iframes that load late can overlap). Backend ads always all show. */
  singleOnly?: boolean;
  /**
   * Side ads from the page's getStaticProps (utils/homeData.ts fetchSideAds):
   * a list renders on the server, `null`/`undefined` keeps the client fetch.
   */
  initialAds?: BannerAd[] | null;
  /** Creative loading; defaults to eager for TOP slots. */
  imageLoading?: "eager" | "lazy";
  /**
   * In-content copy for mobile/tablet, where the page's sidebar is hidden: the
   * ads sit side by side (two across on phones, three from md) instead of
   * stacking in a sidebar column.
   */
  inline?: boolean;
};

const VerticalAds = (props: Props) => {
  const seeded = Array.isArray(props.initialAds);
  const [ads, setAds] = useState<SideAd[]>(seeded ? (props.initialAds as BannerAd[]).map((ad) => ({ ...ad, adType: ad.type })) : []);
  const [loaded, setLoaded] = useState(seeded);
  const [fallbackSlot1Hidden, setFallbackSlot1Hidden] = useState(false);
  const [fallbackSlot2Hidden, setFallbackSlot2Hidden] = useState(false);
  const singleOnly = Boolean(props.singleOnly);
  // Every ad at every width, one per line on phones (centred, up to 300px).
  // The inline copy puts them three across from md, where the page's sidebar
  // is still hidden (tablet on the tag page); a lone ad keeps the full 300px.
  const listClassName = props.inline
    ? "w-full flex flex-col items-center gap-3 md:flex-row md:flex-wrap md:justify-center"
    : "w-full flex flex-col items-center gap-3 md:gap-2";
  const itemClassName = props.inline
    ? "w-full md:w-[calc(33.333%-8px)] only:!w-full max-w-[300px]"
    : "w-full max-w-[300px]";

  useEffect(() => {
    setFallbackSlot1Hidden(false);
    setFallbackSlot2Hidden(false);
    if (seeded) return;

    const fetchSideAds = async () => {
      try {
        const data = await fetchAdsOnce(
          `${process.env.BACKEND_BASE_URL}/ads?location=${props.type}&subLocation=${props.subLocation}`
        );
        if (data.success && data.data.length > 0) {
          const filtered = data.data.filter((ad: any) => ad.type !== "BANNER");
          setAds(filtered.map((ad: any) => ({ ...ad, adType: ad.type })));
        }
      } catch (err) {
        console.error("Fetch ads error:", err);
      } finally {
        setLoaded(true);
      }
    };

    fetchSideAds();
  }, [props.type, props.subLocation, seeded]);

  const getUrl = (url: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  };

  const renderBackendAd = (ad: SideAd) => (
    <div
      key={ad._id ?? ad.thumbnail ?? ad.title ?? "ad"}
      className={itemClassName}
    >
      {/* One frame per ad type, so a stack of side ads has even heights:
          SQUARE 6:5 (300x250), DOUBLE 1:2 (300x600). See AdCreative. */}
      <AdCreative
        src={ad.thumbnail || (ad.type === "DOUBLE" ? DoubleAds : SquareAds)}
        href={getUrl(ad.url)}
        title={ad.title}
        shape={ad.adType === "DOUBLE" ? "double" : "rect"}
        sizes="300px"
        loading={props.imageLoading ?? (props.subLocation === "TOP" ? "eager" : "lazy")}
      />
    </div>
  );

  if (!loaded) return null;

  // No backend side ads → Google AdSense fallback
  if (ads.length === 0) {
    const showFirstSlot = !fallbackSlot1Hidden;
    const showSecondSlot = !singleOnly && !fallbackSlot2Hidden;

    if (!showFirstSlot && !showSecondSlot) return null;

    return (
      <div className="side-ad-container w-full flex flex-col items-center gap-2 relative">
        {/* AdSense needs the full column width to fill, so these stay stacked. */}
        {showFirstSlot ? (
          <div className="w-full max-w-[300px]">
            <GoogleAd
              client="ca-pub-6015892276154863"
              slot={SIDE_SQUARE_SLOT_1}
              style={{ display: "block" }}
              format="auto"
              responsive="true"
              onAdFailed={() => setFallbackSlot1Hidden(true)}
            />
          </div>
        ) : null}
        {showSecondSlot ? (
          <div className="w-full max-w-[300px]">
            <GoogleAd
              client="ca-pub-6015892276154863"
              slot={SIDE_SQUARE_SLOT_2}
              style={{ display: "block" }}
              format="auto"
              responsive="true"
              onAdFailed={() => setFallbackSlot2Hidden(true)}
            />
          </div>
        ) : null}
      </div>
    );
  }

  const squares = ads.filter((ad) => ad.adType !== "DOUBLE");
  const doubles = ads.filter((ad) => ad.adType === "DOUBLE");
  const hasDouble = doubles.length > 0;

  if (hasDouble) {
    const firstDouble = doubles[0];
    const firstSquare = squares.length > 0 ? squares[0] : null;
    const firstAd = firstSquare || firstDouble;
    const secondAd = firstSquare
      ? firstDouble
      : ads.find((ad) => ad._id !== firstDouble._id) || null;

    return (
      <div className={`${listClassName} relative`}>
        {renderBackendAd(firstAd)}
        {secondAd && renderBackendAd(secondAd)}
      </div>
    );
  }

  // Mode: squares — render only backend squares (up to 3)
  const maxItems = 3;
  const items = squares.slice(0, maxItems).map((ad) => renderBackendAd(ad));

  return (
    <div className={`${listClassName} relative`}>
      {items}
    </div>
  );
};

export default VerticalAds;
